import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterReducerState, SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import * as actions from '../actions/index';
import { SET_DATE_RANGE } from '../actions/index';
import * as selectors from '../reducers/index';
import {
  AIService,
  CommentsService,
  EnquiriesService,
  OrganisationDto,
  StatisticsService,
  TenantDto,
} from '@careapp-group/ng-sdk';
import { differenceInDays, sub } from 'date-fns';
import { PayloadAction } from '../action-factory';
import { getTenantInsightsOrganisationIds } from './statistics.selectors';

export const dashboardRegex = /organisations\/[a-z0-9-]+\/dashboard(\?tab=[a-zA-Z]+)?$/;

@Injectable()
export class StatisticsEffects {
  // Helper to simmer down an orgs list by only enabled.
  enabledOrgs(organisations: OrganisationDto[]): string[] {
    return organisations.filter((org) => !!org && org.enabled).map((org) => org.id as string);
  }

  // Given a modelId and modelName, retrieve wrapped statistics
  // for the year specified by remote config.
  // If remote config is not enabled, or no year specified, do not request.
  getWrappedStatistics = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestWrapped),
      withLatestFrom(
        this.store.select(selectors.statistics.getWrappedEntities),
        this.store.select(selectors.getRemoteConfigState),
      ),
      filter(
        ([action, wrappedState, remoteConfigState]) =>
          !!remoteConfigState.web_wrapped_enabled &&
          remoteConfigState.wrapped_year != null &&
          !wrappedState[action.modelName + ':' + action.modelId],
      ),
      switchMap(([action, _, { wrapped_year }]) => {
        return this.statsService
          .getWrapped({
            modelId: action.modelId,
            modelName: action.modelName as 'Tenant' | 'Organisation',
            year: Number.parseInt(wrapped_year),
          })
          .pipe(
            map((stats) => actions.ResponseSuccessWrapped({ data: stats })),
            catchError((err) =>
              of(
                actions.ResponseFailWrapped({
                  error: err,
                  modelId: action.modelId,
                  modelName: action.modelName,
                  year: Number.parseInt(wrapped_year),
                }),
              ),
            ),
          );
      }),
    ),
  );

  // Get wrapped statistics when the org changes, if remote config allows.
  requestWrappedOnSetOrganisation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.Organisations.SELECT_BY_ID),
      filter((action) => !!(<any>action).payload.id),
      map((action) =>
        actions.RequestWrapped({
          modelId: (action as PayloadAction).payload.id,
          modelName: 'Organisation',
        }),
      ),
    ),
  );

  getCustomersStatistics = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCustomersStatisticsAction),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, orgId, filter]) => {
        return this.statsService
          .getOrganisationCustomerActivity({
            organisationActivityRequestDto: {
              organisationIds: [orgId],
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
            },
          })
          .pipe(
            map((stats) =>
              actions.ResponseSuccessCustomersStatisticsAction({ payload: stats[orgId] }),
            ),
            catchError((err) => of(actions.ResponseFailCustomersStatisticsAction({ error: err }))),
          );
      }),
    ),
  );

  getCustomersStatisticsForTenant = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCustomersStatisticsForTenantAction),
      withLatestFrom(
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, organisationIds, filter]) => {
        return this.statsService
          .getOrganisationCustomerActivity({
            organisationActivityRequestDto: {
              organisationIds,
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
            },
          })
          .pipe(
            map((stats) => {
              const results = Object.keys(stats)
                .map((key) => stats[key])
                .flat();
              return actions.ResponseSuccessCustomersStatisticsAction({ payload: results });
            }),
            catchError((err) => of(actions.ResponseFailCustomersStatisticsAction({ error: err }))),
          );
      }),
    ),
  );

  getOrganisationStatistics = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestOrganisationActivityAction),
      withLatestFrom(
        this.store.select(selectors.getAll(selectors.ORGANISATIONS)),
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, organisations, orgId, tenantInsightsOrganisationIds, filter]) => {
        const organisationIds =
          orgId != null
            ? [orgId]
            : action.useAllEnabledOrganisations
              ? this.enabledOrgs(organisations as OrganisationDto[])
              : tenantInsightsOrganisationIds;

        if (!action.includePrevious) {
          return this.statsService
            .getOrganisationActivity({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: filter.startDate.toISOString(),
                endDate: filter.endDate.toISOString(),
              },
            })
            .pipe(
              map((stats) => actions.ResponseSuccessOrganisationActivityAction({ current: stats })),
              catchError((err) =>
                of(actions.ResponseFailOrganisationActivityAction({ error: err })),
              ),
            );
        } else {
          const previousPeriod = this.calculatePreviousPeriod(filter);
          return forkJoin([
            this.statsService.getOrganisationActivity({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: filter.startDate.toISOString(),
                endDate: filter.endDate.toISOString(),
              },
            }),
            this.statsService.getOrganisationActivity({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: previousPeriod.startDate.toISOString(),
                endDate: previousPeriod.endDate.toISOString(),
              },
            }),
          ]).pipe(
            map(([current, previous]) =>
              actions.ResponseSuccessOrganisationActivityAction({ current, previous }),
            ),
            catchError((err) => of(actions.ResponseFailOrganisationActivityAction({ error: err }))),
          );
        }
      }),
    ),
  );
  getMostActiveOrganisationsForTenant = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestMostActiveOrganisationsForTenant),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, tenantId, filter]) => {
        const previousPeriod = this.calculatePreviousPeriod(filter);
        return forkJoin([
          this.statsService.getOrganisationPostingLeaderboard({
            tenantActivityRequestDto: {
              tenantId,
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
            },
          }),
          this.statsService.getOrganisationPostingLeaderboard({
            tenantActivityRequestDto: {
              tenantId,
              startDate: previousPeriod.startDate.toISOString(),
              endDate: previousPeriod.endDate.toISOString(),
            },
          }),
        ]).pipe(
          map(([current, previous]) =>
            actions.ResponseSuccessMostActiveOrganisationsForTenant({ current, previous }),
          ),
          catchError((err) =>
            of(actions.ResponseFailMostActiveOrganisationsForTenant({ error: err })),
          ),
        );
      }),
    ),
  );
  getMostConnectedOrganisationsForTenant = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestMostConnectedOrganisationsForTenant),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, tenantId, filter]) => {
        const previousPeriod = this.calculatePreviousPeriod(filter);
        return forkJoin([
          this.statsService.getMostConnectedOrganisations({
            tenantActivityRequestDto: {
              tenantId,
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
            },
          }),
          this.statsService.getMostConnectedOrganisations({
            tenantActivityRequestDto: {
              tenantId,
              startDate: previousPeriod.startDate.toISOString(),
              endDate: previousPeriod.endDate.toISOString(),
            },
          }),
        ]).pipe(
          map(([current, previous]) =>
            actions.ResponseSuccessMostConnectedOrganisationsForTenant({ current, previous }),
          ),
          catchError((err) =>
            of(actions.ResponseFailMostConnectedOrganisationsForTenant({ error: err })),
          ),
        );
      }),
    ),
  );

  getOrganisationContentCountForTenantOrOrganisation = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestOrganisationContentCountPerDay),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.getAll(selectors.ORGANISATIONS)),
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(
        ([action, organisationId, organisations, tenantInsightsOrganisationIds, filter]) => {
          const timespan = differenceInDays(filter.endDate, filter.startDate);
          const prevPeriodEnd = sub(filter.endDate, { days: timespan + 1 });
          const prevPeriodStart = sub(filter.startDate, {
            days: timespan + 1,
          });
          const organisationIds =
            organisationId != null
              ? [organisationId]
              : action.useAllEnabledOrganisations
                ? this.enabledOrgs(organisations as OrganisationDto[])
                : tenantInsightsOrganisationIds;

          return forkJoin([
            this.statsService.getContentCountPerDay({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: filter.startDate.toISOString(),
                endDate: filter.endDate.toISOString(),
              },
            }),
            this.statsService.getContentCountPerDay({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: prevPeriodStart.toISOString(),
                endDate: prevPeriodEnd.toISOString(),
              },
            }),
          ]).pipe(
            map(([current, previous]) =>
              actions.ResponseSuccessOrganisationContentCountPerDayAction({ current, previous }),
            ),
            catchError((err) =>
              of(actions.ResponseFailOrganisationContentCountPerDayAction({ error: err })),
            ),
          );
        },
      ),
    ),
  );

  getOrganisationContentPerCustomerForSelectedTenantOrOrganisation = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestOrganisationDaysBetweenContent),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.getAll(selectors.ORGANISATIONS)),
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(
        ([action, organisationId, organisations, tenantInsightsOrganisationIds, filter]) => {
          const timespan = differenceInDays(filter.endDate, filter.startDate);
          const prevPeriodEnd = sub(filter.endDate, { days: timespan + 1 });
          const prevPeriodStart = sub(filter.startDate, {
            days: timespan + 1,
          });
          const organisationIds =
            organisationId != null
              ? [organisationId]
              : action.useAllEnabledOrganisations
                ? this.enabledOrgs(organisations as OrganisationDto[])
                : tenantInsightsOrganisationIds;

          return forkJoin([
            this.statsService.getAverageTimeBetweenContent({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: filter.startDate.toISOString(),
                endDate: filter.endDate.toISOString(),
              },
            }),
            this.statsService.getAverageTimeBetweenContent({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: prevPeriodStart.toISOString(),
                endDate: prevPeriodEnd.toISOString(),
              },
            }),
          ]).pipe(
            map(([current, previous]) =>
              actions.ResponseSuccessOrganisationDaysBetweenContentAction({ current, previous }),
            ),
            catchError((err) =>
              of(actions.ResponseFailOrganisationDaysBetweenContentAction({ error: err })),
            ),
          );
        },
      ),
    ),
  );

  getEngagementForSelectedTenantOrOrganisation = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestOrganisationEngagement),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.getAll(selectors.ORGANISATIONS)),
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(
        ([action, organisationId, organisations, tenantInsightsOrganisationIds, filter]) => {
          // Org level insights: Org ID is set, use that.
          // useAllEnabledOrganisations flag set: All enabled orgs
          // fallback: tenant insights filter org IDs.
          const organisationIds =
            organisationId != null
              ? [organisationId]
              : action.useAllEnabledOrganisations
                ? this.enabledOrgs(organisations as OrganisationDto[])
                : tenantInsightsOrganisationIds;

          return this.statsService
            .getEngagementCountPerDay({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: filter.startDate.toISOString(),
                endDate: filter.endDate.toISOString(),
              },
            })
            .pipe(
              map((stats) => actions.ResponseSuccessEngagement({ data: stats })),
              catchError((err) => of(actions.ResponseFailEngagement({ error: err }))),
            );
        },
      ),
    ),
  );
  getFamilyLeaderboardForSelectedTenantOrOrganisation = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestFamilyLeaderboardAction),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, organisationId, tenantInsightsOrganisationIds, filter]) => {
        const organisationIds =
          organisationId != null ? [organisationId] : tenantInsightsOrganisationIds;

        const prevPeriod = this.calculatePreviousPeriod(filter);
        return forkJoin([
          this.statsService.getmostActiveFamilyList({
            organisationActivityRequestDto: {
              organisationIds,
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
            },
            limit: 10,
          }),
          this.statsService.getmostActiveFamilyList({
            organisationActivityRequestDto: {
              organisationIds,
              startDate: prevPeriod.startDate.toISOString(),
              endDate: prevPeriod.endDate.toISOString(),
            },
            limit: 10,
          }),
        ]).pipe(
          map(([current, previous]) =>
            actions.ResponseSuccessFamilyLeaderboardAction({ current, previous }),
          ),
          catchError((err) => of(actions.ResponseFailFamilyLeaderboardAction({ error: err }))),
        );
      }),
    ),
  );

  getMembersStatistics = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.CAAction>(actions.REQUEST_MEMBERS_STATISTICS),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, orgId, filter]) => {
        return this.statsService
          .getOrganisationMemberActivity({
            organisationActivityRequestDto: {
              organisationIds: [orgId],
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
            },
          })
          .pipe(
            map((stats) =>
              actions.ResponseSuccessMembersStatisticsAction({ payload: stats[orgId] }),
            ),

            catchError((err) => of(actions.ResponseFailMembersStatisticsAction({ error: err }))),
          );
      }),
    ),
  );

  getMembersStatisticsForTenant = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.CAAction>(actions.RequestMemberStatisticsForTenantAction),
      withLatestFrom(
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, organisationIds, filter]) => {
        return this.statsService
          .getOrganisationMemberActivity({
            organisationActivityRequestDto: {
              organisationIds,
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
            },
          })
          .pipe(
            map((stats) => {
              const results = Object.keys(stats)
                .map((key) => stats[key])
                .flat();
              return actions.ResponseSuccessMembersStatisticsAction({ payload: results });
            }),

            catchError((err) => of(actions.ResponseFailMembersStatisticsAction({ error: err }))),
          );
      }),
    ),
  );

  getTopMoments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestTopMoments),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, tenantId, organisationId, tenantInsightsOrganisationIds, filter]) => {
        let organisationIds: string[] = [];
        let paginator: string;

        if (!!tenantId && !organisationId) {
          organisationIds = tenantInsightsOrganisationIds;
          paginator = 'tenant-insights';
        } else {
          organisationIds = [organisationId];
          paginator = 'organisation-insights';
        }

        return this.statsService
          .getmostActiveMemos({
            organisationActivityRequestDto: {
              organisationIds,
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
            },
            limit: action.count,
          })
          .pipe(
            map((stats) =>
              actions.Memos.responseSuccessAllPaginatedMemos({
                paginator,
                items: stats,
                filter: {},
              }),
            ),
            catchError((err) => of(actions.ResponseFailTopMoments({ error: err }))),
          );
      }),
    ),
  );

  updateStatistics = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.CAAction>(actions.Organisations.SELECT_BY_ID, SET_DATE_RANGE),
      withLatestFrom(this.store.select(selectors.getRouterState)),
      filter(([action, router]) => this.isOnStatsPage(router)),
      switchMap((action) => this.requestAllStats()),
    ),
  );

  /**
   * *Once* per time we visit the stats page - and again any time the selected organisation id changes (while on that page):
   * - Request all customers
   * - Request all staff
   * So that we can see nams and profile images of all communities/team members in the stats leaderboard
   */

  requestAllRelatedData = createEffect(() =>
    this.store.select(selectors.getRouterState).pipe(
      map((router) => this.isOnStatsPage(router)),
      distinctUntilChanged(),
      filter((isOnStatsPage) => isOnStatsPage),
      switchMap(() =>
        this.store.pipe(
          select(selectors.getSelectedId(selectors.ORGANISATIONS)),
          filter((id) => !!id),
          distinctUntilChanged(),
        ),
      ),
      switchMap(() =>
        of(
          // Request every customer member since every customer member is represented in the stats and we don't paginate the stats table.
          actions.Customers.RequestAllAction({ skipPagination: true }),
          // Request every staff member since every staff member is represented in the stats and we don't paginate the stats table.
          actions.Members.RequestAllAction({ skipPagination: true }),
        ),
      ),
    ),
  );

  // Handles recent memo comment requests for both tenant and org insight pages.
  // If a selected organisation ID is set, will scope requested comments to that.
  requestRecentMemoComments = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestRecentMemoComments),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(([action, tenantId, organisationId, tenantInsightsOrganisationIds, filter]) => {
        // Org level insights: Org ID is set, use that.
        // fallback: tenant insights filter org IDs.
        const organisationIds =
          action.organisationIds ??
          (organisationId ? [organisationId] : tenantInsightsOrganisationIds);

        return this.commentsService
          .listCommentsV2({
            listCommentsQueryDto: {
              tenantId,
              organisationIds,
              startDate: filter.startDate.toISOString(),
              endDate: filter.endDate.toISOString(),
              limit: action.count ?? undefined,
              skip: 0,
            },
          })
          .pipe(
            map((stats) => actions.ResponseSuccessRecentMemoComments({ data: stats })),
            catchError((err) => of(actions.ResponseFailRecentMemoComments({ error: err }))),
          );
      }),
    ),
  );

  requestRecentMemoCommentsSummary = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.ResponseSuccessRecentMemoComments),
      withLatestFrom(
        this.store.select(selectors.getSelected(selectors.TENANTS)),
        this.store.select(selectors.getSelected(selectors.ORGANISATIONS)),
      ),
      filter(([_, tenant, organisation]) => {
        const tenantHasAi = (tenant as TenantDto)?.plan?.hasAi === true;
        const orgHasAi = (organisation as OrganisationDto)?.features?.hasAi === true;

        // The tenant and any selected organisation must both have AI enabled.
        // If no organisation is selected (eg tenant insights), just go by tenant.
        return tenantHasAi && (!!organisation ? orgHasAi : true);
      }),
      switchMap(([action, tenant, _]) => {
        const commentIds = action.data.map((comment) => comment.id);
        return this.aiService
          .summariseMemoComments({
            aiCommentSummaryRequestDto: { commentIds, tenantId: tenant.id },
          })
          .pipe(
            map((summary) =>
              actions.ResponseSuccessRecentMemoCommentsSummary({ data: summary.summary }),
            ),
            catchError((err) => of(actions.ResponseFailRecentMemoCommentsSummary({ error: err }))),
          );
      }),
    ),
  );

  getEnquriesForSelectedTenantOrOrganisation = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestEnquiriesStatsAction),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.getAll(selectors.ORGANISATIONS)),
        this.store.select(getTenantInsightsOrganisationIds),
        this.store.select(selectors.statistics.getFilters),
      ),
      switchMap(
        ([action, organisationId, organisations, tenantInsightsOrganisationIds, filter]) => {
          // Org level insights: Org ID is set, use that.
          // useAllEnabledOrganisations flag set: All enabled orgs
          // fallback: tenant insights filter org IDs.
          const organisationIds =
            organisationId != null
              ? [organisationId]
              : action.useAllEnabledOrganisations
                ? this.enabledOrgs(organisations as OrganisationDto[])
                : tenantInsightsOrganisationIds;

          const prevPeriod = this.calculatePreviousPeriod(filter);
          return forkJoin([
            this.enquiriesService.getStatisticsForOrgs({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: filter.startDate.toISOString(),
                endDate: filter.endDate.toISOString(),
              },
            }),
            this.enquiriesService.getStatisticsForOrgs({
              organisationActivityRequestDto: {
                organisationIds,
                startDate: prevPeriod.startDate.toISOString(),
                endDate: prevPeriod.endDate.toISOString(),
              },
            }),
          ]).pipe(
            map(([current, previous]) =>
              actions.ResponseSuccessEnquiriesStatsAction({ current, previous }),
            ),
            catchError((err) => of(actions.ResponseFailEnquiriesStatsAction({ error: err }))),
          );
        },
      ),
    ),
  );

  private isOnStatsPage(router: RouterReducerState<SerializedRouterStateSnapshot>) {
    return router && router.state ? dashboardRegex.test(router.state.url) : false;
  }

  private calculatePreviousPeriod({ startDate, endDate }: { startDate: Date; endDate: Date }) {
    const timespan = differenceInDays(endDate, startDate);
    const prevPeriodEnd = sub(endDate, { days: timespan + 1 });
    const prevPeriodStart = sub(startDate, {
      days: timespan + 1,
    });
    return { startDate: prevPeriodStart, endDate: prevPeriodEnd };
  }

  requestAllStats() {
    return of(
      actions.RequestCustomersStatisticsAction(),
      actions.RequestOrganisationActivityAction({ includePrevious: true }),
      actions.RequestMembersStatisticsAction(),
      actions.Announcements.RequestTotalCount(),
      actions.MemoCategories.RequestAllAction(),
      actions.CheckIns.RequestRetrieveAllAction({}),
      actions.Feedback.RequestRetrieveAllAction({}),
    );
  }

  constructor(
    private actions$: Actions,
    private statsService: StatisticsService,
    private commentsService: CommentsService,
    private enquiriesService: EnquiriesService,
    private aiService: AIService,
    private store: Store<any>,
  ) {}
}
